import { zodResolver } from '@hookform/resolvers/zod';
import { lazy, memo, Suspense, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { postRegisterAdditionalInfoRequestSchema } from 'services/idm/post/schemas/post-register-additional-info.schema';
import type {
	PostRegisterAdditionalInfoRequest,
	PostRegisterAdditionalInfoResponse,
} from 'services/idm/post/types';

import { useAppTranslation } from 'hooks/use-translates';

import styles from './additional-info-form.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const AdditionalInfoFieldRow = lazy(
	() =>
		import(
			'@login/components/additional-info-field-row/additional-info-field-row'
		)
);

type AdditionalInfoFormProps = {
	formFields: PostRegisterAdditionalInfoRequest['fields'];
	onSubmit?: (
		payload: PostRegisterAdditionalInfoRequest
	) => Promise<PostRegisterAdditionalInfoResponse>;
	isSubmitting?: boolean;
};

function AdditionalInfoFormInner({
	formFields,
	onSubmit,
	isSubmitting,
}: AdditionalInfoFormProps) {
	const { t } = useAppTranslation();
	const { control, register, handleSubmit } =
		useForm<PostRegisterAdditionalInfoRequest>({
			resolver: zodResolver(postRegisterAdditionalInfoRequestSchema),
			defaultValues: { fields: formFields },
		});

	const handleOnSubmit = useCallback(
		async (data: PostRegisterAdditionalInfoRequest) => {
			await onSubmit?.(data);
		},
		[onSubmit]
	);

	return (
		<Suspense fallback={null}>
			<form
				onSubmit={handleSubmit(handleOnSubmit)}
				id="form-register-additional-info"
				noValidate
				className={styles.form}
			>
				<div className={styles.fields}>
					{formFields.map((field, index) => (
						<AdditionalInfoFieldRow
							key={`${index}-${field.alias}`}
							index={index}
							field={field}
							control={control}
							register={register}
						/>
					))}
				</div>

				<Button
					type="submit"
					label={t('register.register_button')}
					isLoading={isSubmitting}
					disabled={isSubmitting}
					variant="primary"
					fullWidth={true}
					dimension="large"
				/>
			</form>
		</Suspense>
	);
}

export const AdditionalInfoForm = memo(AdditionalInfoFormInner);
export default AdditionalInfoForm;
